"use client";

import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";

import { getChatWidgetWsOrigin } from "@/shared/constants/private";

export type ChatWidgetAgentReply = {
    id: string;
    role: "agent";
    text: string;
    createdAt?: string;
};

export type ChatWidgetUserMessage = {
    id: string;
    sessionId: string;
    direction: "inbound";
    role: "user";
    text: string | null;
    eventType: string | null;
    createdAt: string;
};

let sharedSocket: Socket | null = null;

const getChatSocket = (): Socket => {
    if (typeof window === "undefined") {
        throw new Error("Chat socket is only available in the browser");
    }

    if (!sharedSocket) {
        sharedSocket = io(`${getChatWidgetWsOrigin()}/chat-widget`, {
            path: "/api/socket.io",
            transports: ["websocket", "polling"],
            withCredentials: true,
            autoConnect: true,
        });
    }

    return sharedSocket;
};

export { getChatSocket };

export const useChatWidgetRepliesSocket = (
    sessionId: string,
    onReply: (message: ChatWidgetAgentReply) => void,
) => {
    const onReplyRef = useRef(onReply);
    onReplyRef.current = onReply;

    useEffect(() => {
        if (!sessionId || typeof window === "undefined") return;

        const socket = getChatSocket();

        const handleReply = (message: ChatWidgetAgentReply) => {
            onReplyRef.current(message);
        };

        const subscribe = () => {
            socket.emit("subscribe", { sessionId });
        };

        socket.on("connect", subscribe);
        socket.on("agent_reply", handleReply);
        if (socket.connected) {
            subscribe();
        }

        return () => {
            socket.off("connect", subscribe);
            socket.off("agent_reply", handleReply);
        };
    }, [sessionId]);
};

type AdminSocketHandlers = {
    onUserMessage?: (message: ChatWidgetUserMessage) => void;
    onAgentReply?: (message: ChatWidgetAgentReply) => void;
    onSessionActivity?: (payload: { sessionId: string }) => void;
};

export const useChatWidgetAdminSocket = (activeSessionId: string, handlers: AdminSocketHandlers) => {
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    useEffect(() => {
        if (typeof window === "undefined") return;

        const socket = getChatSocket();

        const subscribeAdmin = () => {
            socket.emit("subscribe_admin");
        };

        const subscribeSession = () => {
            if (activeSessionId) {
                socket.emit("subscribe", { sessionId: activeSessionId });
            }
        };

        const handleUserMessage = (message: ChatWidgetUserMessage) => {
            handlersRef.current.onUserMessage?.(message);
        };

        const handleAgentReply = (message: ChatWidgetAgentReply) => {
            handlersRef.current.onAgentReply?.(message);
        };

        const handleSessionActivity = (payload: { sessionId: string }) => {
            handlersRef.current.onSessionActivity?.(payload);
        };

        socket.on("connect", subscribeAdmin);
        socket.on("connect", subscribeSession);
        socket.on("user_message", handleUserMessage);
        socket.on("agent_reply", handleAgentReply);
        socket.on("session_activity", handleSessionActivity);

        if (socket.connected) {
            subscribeAdmin();
            subscribeSession();
        }

        return () => {
            socket.off("connect", subscribeAdmin);
            socket.off("connect", subscribeSession);
            socket.off("user_message", handleUserMessage);
            socket.off("agent_reply", handleAgentReply);
            socket.off("session_activity", handleSessionActivity);
        };
    }, [activeSessionId]);
};
